import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import Cookies from "js-cookie";
import { JoinCallModal } from "../elements/JoinCallModal";
import { JoinChatModal } from "../elements/JoinChatModal";
import SocketContext from "./SocketProvider";
import { setCallerData } from "../../store/slice/callingSlice";
import { getProfileDetails } from "../../service/authService";
import {
  bindRingtoneUnlock,
  bindRingtoneCrossTab,
  startRingtone,
  stopRingtone,
  getRingtoneAudio,
  subscribeRingtoneBlocked,
  subscribeRingtoneStopped,
  isIOS,
} from "../../util/ringtone";
import { stopAllCallMedia } from "../../util/stopAllCallMedia";
import { stopAllRegisteredMedia } from "../../util/mediaTrackRegistry";
import { isUserInAnotherActiveCall } from "../../util/sessionTabLock";
// stopRingtone used on mount to clear accidental mobile media session
import { normalizeRingPayload, mediaUrl, personName } from "../../util/callPeerDisplay";
import { isLiveCallPath } from "../../util/callPathHelpers";

/**
 * Global host for incoming Call Now / chat / video ring.
 * Mounted once under BrowserRouter — survives every page change.
 *
 * Uses socketRef + stable handlers (NOT useSocket with changing deps),
 * so listeners are not torn down while a call is ringing.
 */
function getUserDetails() {
  try {
    return JSON.parse(localStorage.getItem("userDetails") || "{}");
  } catch {
    return {};
  }
}

function getMyId() {
  const ud = getUserDetails();
  return String(ud?._id || ud?.id || "");
}

function getMyRole() {
  const ud = getUserDetails();
  return String(ud?.roleCode || ud?.role || "");
}

function isForMeConsultant(data) {
  const myId = getMyId();
  if (!myId || !data) return false;
  const role = getMyRole();
  if (role && role !== "consultant") return false;

  const rawTargets = [
    data?.consultantId,
    data?.consultant?.id,
    data?.consultant?._id,
    data?.consultant,
    data?.callerData?.consultant?.id,
    data?.callerData?.consultant?._id,
    data?.callerData?.consultant,
    data?.consultantDetails?._id,
    data?.consultantDetails?.id,
    data?.consultantDetails,
    data?.data?.consultantId,
    data?.data?.consultant?.id,
    data?.data?.consultant?._id,
  ];

  const targets = [];
  for (const item of rawTargets) {
    if (!item) continue;
    if (typeof item === "string" || typeof item === "number") {
      const s = String(item).trim();
      if (s && s !== "[object Object]") targets.push(s);
    } else if (typeof item === "object") {
      if (item._id) targets.push(String(item._id));
      if (item.id) targets.push(String(item.id));
    }
  }

  if (targets.length === 0) return false;
  return targets.includes(myId);
}

export default function IncomingCallHost() {
  const dispatch = useDispatch();
  const { socket, socketRef } = useContext(SocketContext) || {};
  const location = useLocation();
  const callerData = useSelector((s) => s?.callingSlice?.data);
  const profile = useSelector((s) => s?.initialProfileSlice?.data);

  const [isOpen, setIsOpen] = useState(false);
  const [isChatModalOpen, setIsChatModalOpen] = useState(false);
  const [appointment, setAppointment] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [sessionToken, setSessionToken] = useState(null);

  const audioRef = useRef(null);
  if (!audioRef.current) {
    audioRef.current = getRingtoneAudio();
  }

  const openApptRef = useRef(null);
  const isOpenRef = useRef(false);
  const isChatOpenRef = useRef(false);
  const recoveringRef = useRef(false);
  const pathRef = useRef(location.pathname);

  isOpenRef.current = isOpen;
  isChatOpenRef.current = isChatModalOpen;
  pathRef.current = location.pathname;

  const onLiveCallPath = (path) => {
    return (
      isLiveCallPath(path) ||
      isLiveCallPath(typeof window !== "undefined" ? window.location?.pathname : "")
    );
  };

  useEffect(() => {
    bindRingtoneUnlock();
    bindRingtoneCrossTab();
    // Never leave a sticky ring when app/shell mounts (mobile media session bug)
    stopRingtone("stop", null);
  }, []);

  // Recording-indicator fix: previously the only safety-net sweep for a
  // leaked camera/mic stream (registerMediaStream / registerFromPublisher)
  // lived in AppointmentInProgress.js's mount effect — so if a call ended and
  // the user landed on any OTHER page (dashboard, profile, home, ...), a
  // stream that leaked past the call page's own cleanup was never stopped and
  // the browser tab's recording indicator stayed lit indefinitely. This host
  // is mounted once globally and survives every route change, so sweep here
  // instead whenever we are NOT on a live call page.
  useEffect(() => {
    if (onLiveCallPath(location.pathname)) return;
    import("../../util/stopAllCallMedia")
      .then(({ stopAllCallMedia }) => stopAllCallMedia())
      .catch(() => {});
    import("../../util/mediaTrackRegistry")
      .then(({ stopAllRegisteredMedia }) => stopAllRegisteredMedia())
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  useEffect(() => {
    return subscribeRingtoneStopped((payload) => {
      // Only close UI for real accept/decline from another tab — not local cleanup noise
      const reason = payload?.reason || "";
      const stoppedApptId = payload?.appointmentId ? String(payload.appointmentId) : null;
      if (openApptRef.current && stoppedApptId && String(openApptRef.current) !== stoppedApptId) {
        return;
      }
      if (
        ["accepted", "decline", "reject", "disconnect", "chat-accepted"].includes(reason)
      ) {
        setIsOpen(false);
        setIsChatModalOpen(false);
        openApptRef.current = null;
      }
    });
  }, []);

  useEffect(() => {
    if (!(isOpen || isChatModalOpen)) return undefined;
    return subscribeRingtoneBlocked((blocked) => {
      if (blocked && isIOS()) {
        toast.info("Incoming call — tap anywhere to enable the ringtone.", {
          autoClose: 15000,
          toastId: "ring-audio-blocked",
        });
      }
    });
  }, [isOpen, isChatModalOpen]);

  // Ringtone STOP-ON-CLOSE only. openVideoCall()/openChat() already call
  // startRingtone() directly the moment the ring data arrives, right before
  // setIsOpen(true)/setIsChatModalOpen(true) — this effect firing right after
  // (because it also watches isOpen/isChatModalOpen) used to call
  // startRingtone() a second time for the same call. Two near-simultaneous
  // audio.play() calls on the shared <audio> element could interrupt/reject
  // the first play() promise (common browser behavior right after a
  // load()+play()), which tripped the OT_-style fallback beep path in
  // ringtone.js's attemptPlay() — so the real ringtone.mp3 and the synthetic
  // fallback beep could end up audible at the same time. Cleanup (stop on
  // close) still needs to live here since it must run whenever the modal
  // closes regardless of which call site started the ring.
  const wasAnyOpenRef = useRef(false);
  useEffect(() => {
    const isAnyOpen = isOpen || isChatModalOpen;
    if (!isAnyOpen && wasAnyOpenRef.current) {
      // Modals were open and are now both closed
      const isJoining = onLiveCallPath(pathRef.current) || localStorage.getItem("joined") === "true";
      if (!isJoining) {
        try {
          stopRingtone("modal-close", openApptRef.current);
          stopAllCallMedia();
          stopAllRegisteredMedia();
        } catch (_) {
          /* ignore */
        }
      }
      openApptRef.current = null;
    }
    wasAnyOpenRef.current = isAnyOpen;
  }, [isOpen, isChatModalOpen]);

  const openVideoCall = useCallback(
    (data, source = "socket") => {
      const apptId = data?.appointmentId ? String(data.appointmentId) : null;
      if (onLiveCallPath(pathRef.current) || isUserInAnotherActiveCall(apptId)) return;
      if (!isForMeConsultant(data)) {
        console.warn("[IncomingCallHost] video ring ignored (not for me)", {
          source,
          myId: getMyId(),
          consultantId: data?.consultantId,
        });
        return;
      }

      if (apptId && openApptRef.current === apptId && isOpenRef.current) return;

      try {
        if (apptId) sessionStorage.removeItem(`ringDismissed:${apptId}`);
      } catch (_) {
        /* ignore */
      }

      if (data?.sessionId) setSessionId(data.sessionId);
      if (data?.token || data?.sessionToken) {
        setSessionToken(data.token || data.sessionToken);
      }

      // Normalize name + image URL so modal never shows consultant self / broken img
      const normalized = normalizeRingPayload({
        ...data,
        appointmentId: apptId || data?.appointmentId,
        consultantId:
          data?.consultantId ||
          data?.consultant?.id ||
          data?.callerData?.consultant?.id,
        clientId: data?.clientId || data?.callerData?.user?.id || data?.user?.id,
        details: data?.details,
        appointmentStartTime: data?.details?.appointmentStartTime || data?.appointmentStartTime,
        appointmentEndTime: data?.details?.appointmentEndTime || data?.appointmentEndTime,
        service:
          data?.service || (data?.appointmentEndTime || data?.details?.appointmentEndTime ? undefined : "direct-access-online-psychological-counseling"),
      });

      dispatch(setCallerData(normalized));
      setAppointment(normalized);
      openApptRef.current = apptId;
      try {
        localStorage.setItem("appointmentData", JSON.stringify(normalized));
      } catch (_) {
        /* ignore */
      }

      const peerName = personName(normalized?.callerData?.user) || "client";
      console.log("[IncomingCallHost] OPEN video/voice modal", {
        source,
        apptId,
        peerName,
        peerImage: mediaUrl(normalized?.callerData?.user?.image),
      });
      toast.info(`Incoming call from ${peerName}`, {
        autoClose: 4000,
        toastId: "incoming-call",
      });
      startRingtone(apptId);
      setIsChatModalOpen(false);
      setIsOpen(true);
    },
    [dispatch],
  );

  const openChat = useCallback(
    (data, source = "socket") => {
      const apptId = data?.appointmentId ? String(data.appointmentId) : null;
      if (onLiveCallPath(pathRef.current) || isUserInAnotherActiveCall(apptId)) return;
      if (!isForMeConsultant(data)) {
        console.warn("[IncomingCallHost] chat ring ignored (not for me)", {
          source,
          myId: getMyId(),
          consultantId: data?.consultantId,
        });
        return;
      }
      try {
        if (apptId) sessionStorage.removeItem(`ringDismissed:${apptId}`);
      } catch (_) {
        /* ignore */
      }

      const normalized = normalizeRingPayload({
        ...data,
        appointmentId: apptId || data?.appointmentId,
        consultantId:
          data?.consultantId ||
          data?.id ||
          data?.callerData?.consultant?.id ||
          data?.consultant?.id,
        clientId: data?.clientId || data?.callerData?.user?.id,
        details: data?.details,
        appointmentStartTime: data?.details?.appointmentStartTime || data?.appointmentStartTime,
        appointmentEndTime: data?.details?.appointmentEndTime || data?.appointmentEndTime,
        mode: data?.mode || "chat",
      });

      dispatch(setCallerData(normalized));
      setAppointment(normalized);
      openApptRef.current = apptId;
      try {
        localStorage.setItem("appointmentData", JSON.stringify(normalized));
      } catch (_) {
        /* ignore */
      }

      const peerName = personName(normalized?.callerData?.user) || "client";
      console.log("[IncomingCallHost] OPEN chat modal", { source, apptId, peerName });
      toast.info(`Incoming chat from ${peerName}`, {
        autoClose: 4000,
        toastId: "incoming-chat",
      });
      startRingtone(apptId);
      setIsOpen(false);
      setIsChatModalOpen(true);
    },
    [dispatch],
  );

  // ── Stable socket listeners (register once per socket instance) ──
  useEffect(() => {
    const s = socket || socketRef?.current;
    if (!s) return undefined;

    const onConsultantCall = (data) => openVideoCall(data, "consultantCallResponse");
    const onIndirectCall = (data) =>
      openVideoCall(
        {
          ...data,
          consultantId: data?.consultantId || data?.id || data?.callerData?.consultant?.id,
          user: data?.callerData?.user || data?.user,
          consultant: data?.callerData?.consultant || data?.consultant,
        },
        "consultantIndirectCallResponse",
      );
    const onChat = (data) => openChat(data, "chatSessionResponse");
    const onChatIndirect = (data) =>
      openChat(
        {
          ...data,
          consultantId: data?.consultantId || data?.id,
          user: data?.callerData?.user || data?.user,
          consultant: data?.callerData?.consultant || data?.consultant,
        },
        "chatSessionIndirectResponse",
      );
    const onTokBox = (data) => {
      if (!isForMeConsultant(data) && !isForMeConsultant({ consultantId: data?.callerData?.consultant?.id })) {
        return;
      }
      const incomingApptId = data?.appointmentId ? String(data.appointmentId) : null;
      if (openApptRef.current && incomingApptId && String(openApptRef.current) !== incomingApptId) {
        return;
      }
      if (data?.sessionId) setSessionId(data.sessionId);
      if (data?.token) setSessionToken(data.token);
      setAppointment((prev) => ({ ...(prev || {}), ...data }));
      try {
        const stored = JSON.parse(localStorage.getItem("appointmentData") || "{}");
        localStorage.setItem(
          "appointmentData",
          JSON.stringify({
            ...stored,
            ...data,
            sessionId: data.sessionId || stored.sessionId,
            token: data.token || stored.token,
          }),
        );
      } catch (_) {
        /* ignore */
      }
    };
    const onCloseRing = (data) => {
      const incomingApptId = data?.appointmentId ? String(data.appointmentId) : null;
      if (openApptRef.current && incomingApptId && String(openApptRef.current) !== incomingApptId) {
        return;
      }
      setIsOpen(false);
      setIsChatModalOpen(false);
      openApptRef.current = null;
      stopRingtone(
        data?.joined ? "chat-accepted" : data?.disconnect ? "reject" : "disconnect",
        data?.appointmentId,
      );
    };
    const onVideoDisconnect = (data) => {
      if (data?.disconnect !== true) return;
      const incomingApptId = data?.appointmentId ? String(data.appointmentId) : null;
      if (openApptRef.current && incomingApptId && String(openApptRef.current) !== incomingApptId) {
        return;
      }
      setIsOpen(false);
      setIsChatModalOpen(false);
      openApptRef.current = null;
      stopRingtone("disconnect", data?.appointmentId);
    };

    s.on("consultantCallResponse", onConsultantCall);
    s.on("consultantIndirectCallResponse", onIndirectCall);
    s.on("chatSessionResponse", onChat);
    s.on("chatSessionIndirectResponse", onChatIndirect);
    s.on("vonageTokBoxSessionResponse", onTokBox);
    s.on("chatJoinConfirmResponse", onCloseRing);
    s.on("chatSessionRejectResponse", onCloseRing);
    s.on("vonageSessionRejectResponse", onCloseRing);
    s.on("videoSessionDisconnectResponse", onVideoDisconnect);

    console.log("[IncomingCallHost] listeners attached", { socketId: s.id, connected: s.connected });

    return () => {
      s.off("consultantCallResponse", onConsultantCall);
      s.off("consultantIndirectCallResponse", onIndirectCall);
      s.off("chatSessionResponse", onChat);
      s.off("chatSessionIndirectResponse", onChatIndirect);
      s.off("vonageTokBoxSessionResponse", onTokBox);
      s.off("chatJoinConfirmResponse", onCloseRing);
      s.off("chatSessionRejectResponse", onCloseRing);
      s.off("vonageSessionRejectResponse", onCloseRing);
      s.off("videoSessionDisconnectResponse", onVideoDisconnect);
    };
  }, [socket, socketRef, openVideoCall, openChat]);

  /** Recover ring from API if socket event was missed */
  const recoverRingFromProfile = useCallback(async () => {
    if (onLiveCallPath(pathRef.current)) return;
    if (isOpenRef.current || isChatOpenRef.current || recoveringRef.current) return;
    const token = localStorage.getItem("auth_token") || Cookies.get("auth_token");
    if (!token) return;
    const role = getUserDetails()?.roleCode || profile?.roleCode;
    if (role !== "consultant") return;

    recoveringRef.current = true;
    try {
      const response = await getProfileDetails(token);
      if (onLiveCallPath(pathRef.current)) return;
      if (response?.data?.status !== 200) return;
      const docs = response?.data?.docs;
      const RING_MAX_AGE_MS = 120 * 1000;
      const appt = docs?.appointmentData;
      const ringing = docs?.ringingStatus;
      const apptStatus = appt?.status;
      const apptFresh =
        appt?.createdAt || appt?.updatedAt
          ? Date.now() - new Date(appt.updatedAt || appt.createdAt).getTime() <
            RING_MAX_AGE_MS
          : false;
      const isLiveRing =
        ringing?.status === true &&
        appt?.consultantStatus === "notJoin" &&
        (apptStatus === "active" || apptStatus === "inProgress") &&
        apptFresh &&
        sessionStorage.getItem(`ringDismissed:${appt?._id}`) !== "1";

      if (!isLiveRing || !appt?._id) return;

      const myId = getMyId();
      const apptConsultantId = String(appt?.consultantId || appt?.consultantDetails?._id || "");
      if (myId && apptConsultantId && apptConsultantId !== myId) {
        return;
      }

      const clientImg = mediaUrl(appt?.clientDetails?.image);
      const consultantImg = mediaUrl(appt?.consultantDetails?.image);
      const Data = {
        user: {
          name: `${appt?.clientDetails?.firstName || ""} ${
            appt?.clientDetails?.lastName || ""
          }`.trim(),
          email: appt?.clientDetails?.email,
          image: clientImg,
          id: appt?.clientDetails?._id || appt?.clientId,
        },
        consultant: {
          name: `${appt?.consultantDetails?.firstName || ""} ${
            appt?.consultantDetails?.lastName || ""
          }`.trim(),
          email: appt?.consultantDetails?.email,
          image: consultantImg,
          id: appt?.consultantDetails?._id || appt?.consultantId,
        },
        mode: appt?.service?.mode?.slug,
      };
      const finalData = normalizeRingPayload({
        appointmentId: appt?._id,
        consultantId: appt?.consultantId,
        clientId: appt?.clientId,
        appointmentStartTime: appt?.consultantJoinTime || Date.now(),
        mode: appt?.service?.mode?.slug,
        callerData: { ...Data },
        user: Data.user,
        consultant: Data.consultant,
        clientDetails: appt?.clientDetails,
        consultantDetails: appt?.consultantDetails,
        sessionId: appt?.sessionId,
        token: appt?.sessionToken,
        paymentMode: appt?.paymentMode,
        service: "direct-access-online-psychological-counseling",
      });

      if (ringing?.mode === "chat" || finalData.mode === "chat") {
        openChat(finalData, "profile-recover");
      } else {
        openVideoCall(finalData, "profile-recover");
      }
    } catch (e) {
      console.warn("[IncomingCallHost] recover failed", e?.message);
    } finally {
      recoveringRef.current = false;
    }
  }, [openChat, openVideoCall, profile?.roleCode]);

  useEffect(() => {
    recoverRingFromProfile();
  }, [location.pathname, recoverRingFromProfile]);

  useEffect(() => {
    const token = localStorage.getItem("auth_token") || Cookies.get("auth_token");
    if (!token) return undefined;
    const id = setInterval(() => {
      const role = getUserDetails()?.roleCode || profile?.roleCode;
      if (role === "consultant" && !onLiveCallPath(pathRef.current)) {
        recoverRingFromProfile();
      }
    }, 3000);
    return () => clearInterval(id);
  }, [recoverRingFromProfile, profile?.roleCode]);

  useEffect(() => {
    const onVis = () => {
      if (document.visibilityState === "visible") recoverRingFromProfile();
    };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, [recoverRingFromProfile]);

  useEffect(() => {
    const s = socket || socketRef?.current;
    if (!s) return undefined;
    const onConnect = () => {
      console.log("[IncomingCallHost] socket connected — recover ring");
      recoverRingFromProfile();
    };
    s.on("connect", onConnect);
    return () => s.off("connect", onConnect);
  }, [socket, socketRef, recoverRingFromProfile]);

  const live = onLiveCallPath(location.pathname);

  return (
    <>
      {/* Always render modals so Headless UI can portal; hide open when on live call */}
      <JoinCallModal
        isOpen={!live && isOpen}
        setIsOpen={(v) => {
          setIsOpen(!!v);
          if (!v) {
            const isJoining = onLiveCallPath(pathRef.current) || localStorage.getItem("joined") === "true";
            if (!isJoining) {
              stopRingtone("decline", openApptRef.current);
              stopAllCallMedia();
              stopAllRegisteredMedia();
            }
            openApptRef.current = null;
          }
        }}
        // Consultant ring UI must show the CLIENT (not consultant self).
        // Prefer nested callerData.user from server ring payload.
        data={
          (callerData || appointment)?.callerData?.user ||
          (callerData || appointment)?.user ||
          (callerData || appointment)?.callerData?.client ||
          appointment?.clientDetails ||
          null
        }
        callerData={callerData || appointment}
        sessionId={sessionId || callerData?.sessionId || appointment?.sessionId}
        sessionToken={
          sessionToken ||
          callerData?.token ||
          callerData?.sessionToken ||
          appointment?.token ||
          appointment?.sessionToken
        }
        audioRef={audioRef}
        service={"direct-access-online-psychological-counseling"}
      />
      <JoinChatModal
        isOpen={!live && isChatModalOpen}
        setIsOpen={(v) => {
          setIsChatModalOpen(!!v);
          if (!v) {
            const isJoining = onLiveCallPath(pathRef.current) || localStorage.getItem("joined") === "true";
            if (!isJoining) {
              stopRingtone("decline", openApptRef.current);
              stopAllCallMedia();
              stopAllRegisteredMedia();
            }
            openApptRef.current = null;
          }
        }}
        appointmentData={appointment || callerData}
        callerData={callerData}
        audioRef={audioRef}
      />
    </>
  );
}
